import {
  ButtonComponentActionResultTypenames,
  ContextualColor,
  ContextualIcon,
  SelfFundedOfferDetailsSuccess,
  TagColor,
} from '@varo/graphql-schema-types';
import rewardFeaturedCardTextureBgDefault from 'assets/images/vxl/textures/reward-featured-card-texture-bg-default.png';
import { useLocalSearchParams, useRouter } from 'expo-router';
import TitleLockUp from 'features/rewards/components/TitleLockUp/TitleLockUp';
import RenderHtmlOfferSectionView from 'features/rewards/Details/RenderHtmlOfferSectionView/RenderHtmlOfferSectionView';
import useSelfFundedOffersDetailsV2 from 'features/rewards/hooks/selfFundedOffers/useSelfFundedOfferDetailsV2';
import React, { useCallback } from 'react';
import {
  ImageSourcePropType,
  Platform,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { parseOptionalGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import { openExternalUrl } from 'services/deeplinks/helpers/openExternalUrl';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import { isWeb } from 'shared/constants/platform';
import { useViewDidFocusLogScreenAnalytics } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

import SelfFundedOfferDetailsHeader from './Details/SelfFundedOfferDetailsHeader';

const getHtmlContents = (offer: SelfFundedOfferDetailsSuccess) => {
  const disclaimerHtml = offer.disclosureContent?.replace(/\n/g, '<br />');

  return [
    {
      id: 'terms',
      title: offer.termsTitle,
      content: offer.termsContent,
      showDivider: true,
      linkText: undefined,
    },
    {
      id: 'disclaimer',
      title: offer.disclosureTitle,
      content: disclaimerHtml,
      showDivider: false,
      linkText: offer.legalText,
    },
  ];
};

interface ListItemData {
  id: string;
  labelText: string;
  iconKey: string;
}

const SelfFundedOfferDetailsV2 = () => {
  const { offerId } = useLocalSearchParams<{
    offerId: string;
  }>();
  const insets = useSafeAreaInsets();
  const { defaultTitle, defaultMessage, cancelButtonTitle } =
    useDefaultErrorStrings();
  const router = useRouter();
  const { loading, offersData, errorData } =
    useSelfFundedOffersDetailsV2(offerId);
  const analytics = parseOptionalGQLAnalyticsEvent(
    offersData?.displayAnalytics,
  );

  const handlePdfClick = () => {
    if (offersData?.legalUrl) {
      router.push({
        pathname: '/rewards/disclosure',
        params: {
          pdfUrl: offersData.legalUrl,
        },
      });
    }
  };

  const listData: ListItemData[] =
    offersData?.instructions?.map((instruction) => ({
      id: instruction.id,
      labelText: instruction.text,
      iconKey: instruction.icon || 'STORE',
    })) || [];

  useViewDidFocusLogScreenAnalytics(analytics);

  const handleNavigateBack = useCallback((): void => {
    router.back();
  }, [router]);

  const ErrorScreenComponent = (
    <ErrorScreen
      screenType={ErrorScreenType.Error}
      messageHeadline={defaultTitle}
      messageBody={defaultMessage}
      primaryButton={{
        title: cancelButtonTitle ?? 'Close',
        onPress: handleNavigateBack,
      }}
    />
  );

  const footerButtonAction = () => {
    if (
      offersData?.footerButton?.buttonOnClick.__typename ===
      ButtonComponentActionResultTypenames.OpenURLNavigationAction
    ) {
      void openExternalUrl(offersData.footerButton.buttonOnClick.url);
    }
  };

  return (
    <View style={[styles.parentContainer, { paddingBottom: insets.bottom }]}>
      {!isWeb ? (
        <NavigationBar
          title=""
          primaryIcon={<Icon iconKey="CLOSE" iconSize="MD" />}
          primaryOnPress={handleNavigateBack}
        />
      ) : null}
      <View style={styles.modalContainer}>
        {offersData ? (
          <View style={styles.container}>
            <ScrollView showsVerticalScrollIndicator={true}>
              <View>
                <SelfFundedOfferDetailsHeader
                  defaultHeroImage={
                    rewardFeaturedCardTextureBgDefault as ImageSourcePropType
                  }
                  heroImage={offersData?.heroImage}
                  logoImage={undefined}
                />

                <TitleLockUp
                  merchant={offersData?.descriptionItem?.primaryText ?? ''}
                  offerRate={offersData?.offerTitle ?? ''}
                  logoUrl={offersData?.logoImage}
                  tag={
                    offersData?.statusText
                      ? {
                          __typename: 'Tag',
                          id: 'status',
                          text: offersData.statusText,
                          tagColor: TagColor.Positive,
                          color: ContextualColor.Positive,
                          icon: ContextualIcon.ListCheckmark,
                        }
                      : null
                  }
                  validityText={offersData?.expirationText ?? undefined}
                />

                {listData.length > 0 ? (
                  <View style={styles.howItWorksContainer}>
                    {listData.map((item: ListItemData, index: number) => {
                      const isLastItem = index === listData.length - 1;
                      return (
                        <View key={item.id}>
                          <ListItem
                            primaryCopy={item.labelText}
                            showsChevron={false}
                            leadingAccessory={{
                              id: `leading-icon-${item.id}`,
                              name: VXLComponentId.ICON,
                              props: { iconKey: item.iconKey },
                            }}
                          />
                          {!isLastItem && (
                            <View style={styles.divider}>
                              <HairLineDivider />
                            </View>
                          )}
                        </View>
                      );
                    })}
                  </View>
                ) : null}

                <View style={styles.dividerStyle} />

                {getHtmlContents(offersData).map((item) => {
                  if (item.content) {
                    return (
                      <RenderHtmlOfferSectionView
                        id={item.id}
                        key={item.title}
                        content={item.content}
                        titleText={item.title}
                        showDivider={item.showDivider}
                        linkText={item.linkText as string}
                        handlePdfClick={handlePdfClick}
                      />
                    );
                  }
                  return null;
                })}
              </View>
            </ScrollView>

            {offersData.footerButton ? (
              <View style={styles.buttonContainer}>
                <Button
                  label={offersData.footerButton.buttonText}
                  size="LARGE"
                  type="PRIMARY"
                  width="FULL"
                  onPress={footerButtonAction}
                />
              </View>
            ) : null}
          </View>
        ) : null}
        <ScreenTransitionOverlay
          LoadingComponent={<LoadingSpinner />}
          isLoading={loading}
          ErrorComponent={errorData ? ErrorScreenComponent : null}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    backgroundColor: theme.color.background.primary,
    padding: theme.spacing.m,
    width: '100%',
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    width: '100%',
  },
  divider: {
    paddingHorizontal: theme.spacing.m,
  },
  dividerStyle: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
  },
  modalContainer: {
    flex: 1,
  },
  parentContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    ...Platform.select({
      web: {
        maxWidth: 600,
      },
    }),
    alignSelf: 'center',
    width: '100%',
  },
  howItWorksContainer: {
    paddingBottom: theme.spacing.m,
  },
});

export default SelfFundedOfferDetailsV2;
